import {
    clearAllBodyScrollLocks,
    disableBodyScroll,
    enableBodyScroll,
} from 'body-scroll-lock-upgrade';
import FocusTrap from 'focus-trap-react';
import { useEffect, useRef, forwardRef, useCallback } from 'react';

import Portal from '@perpay-web/components/base/Portal/Portal';
import { useEndToEndTestId } from '@perpay-web/hooks/useEndToEndTestId';
import { useMountAndUnmount } from '@perpay-web/hooks/useMountAndUnmount';
import {
    getClassName,
    isInsideScrollableContainer,
} from '@perpay-web/utils/domUtils';
import { isEscapeKey } from '@perpay-web/utils/keyboardUtils';
import { noop } from '@perpay-web/utils/noop';

import styles from './Modal.scss';

/**
 * @typedef {Object} ModalProps
 * @property {React.ReactNode} children - The content to be displayed in the modal
 * @property {string} [className] - Additional class name for the modal overlay
 * @property {string} [containerClassName] - Additional class name for the modal container
 * @property {string} [contentClassName] - Additional class name for the modal content
 * @property {string} [closeButtonClassName] - Additional class name for the close button
 * @property {boolean} [fullscreen=false] - Whether the modal should be fullscreen
 * @property {boolean} [showClose=true] - Whether to show the close button
 * @property {(() => void) | null} [onClose] - Callback when the modal is closed
 * @property {React.CSSProperties} [anchorPosition] - Styles to define position for anchored modals
 * @property {boolean} [keepHeaderAboveOverlay=false] - Whether to keep header above overlay
 * @property {boolean} [allowDismissal=true] - Whether the modal can be dismissed
 * @property {string} [aria-label] - ARIA label for the modal
 * @property {string} [parentId] - ID of the parent element to render the modal in
 * @property {boolean} [lock=true] - Whether to lock body scroll
 * @property {boolean} [returnFocusOnClose=true] - Whether to return focus on close
 */

/**
 * https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/dialog.html
 * @type {React.ForwardRefExoticComponent<ModalProps & React.RefAttributes<any>>}
 */
const StatelessModal = forwardRef(
    (
        {
            className = null,
            // Legacy multiple classname prop. Do not duplicate this pattern.
            // https://perpay.atlassian.net/wiki/spaces/PERPAY/pages/3282567207/Components+should+own+their+own+styles
            // eslint-disable-next-line @perpay-web/no-multiple-classname-props
            containerClassName = null,
            // Legacy multiple classname prop. Do not duplicate this pattern.
            // https://perpay.atlassian.net/wiki/spaces/PERPAY/pages/3282567207/Components+should+own+their+own+styles
            // eslint-disable-next-line @perpay-web/no-multiple-classname-props
            contentClassName = null,
            // Legacy multiple classname prop. Do not duplicate this pattern.
            // https://perpay.atlassian.net/wiki/spaces/PERPAY/pages/3282567207/Components+should+own+their+own+styles
            // eslint-disable-next-line @perpay-web/no-multiple-classname-props
            closeButtonClassName = null,
            fullscreen = false,
            children,
            showClose = true,
            onClose = null,
            anchorPosition = null,
            keepHeaderAboveOverlay = false,
            allowDismissal = true,
            'aria-label': ariaLabel,
        },
        ref,
    ) => {
        const modalRef = useRef();

        const endToEndTestIDParams = useEndToEndTestId();

        useMountAndUnmount(() => {
            const onClick = (e) => {
                if (!document.getElementById('modal-root')) {
                    // We're in storybook or another non-app context
                    return;
                }

                const targetIsOverlay = e.target.classList.contains(
                    styles.modal__overlay,
                );
                if (
                    document.getElementById('modal-root').contains(e.target) &&
                    !targetIsOverlay
                ) {
                    return;
                }

                if (!document.documentElement.contains(e.target)) {
                    return;
                }

                if (!allowDismissal) {
                    return;
                }

                onClose();
            };

            const onKeyDown = (event) => {
                if (isEscapeKey(event.key) && onClose && allowDismissal) {
                    onClose();
                }
            };

            document.addEventListener('keydown', onKeyDown);
            modalRef.current.addEventListener('click', onClick);

            const modalRefCopy = modalRef.current;

            return () => {
                document.removeEventListener('keydown', onKeyDown);
                if (!modalRefCopy) {
                    return;
                }
                modalRefCopy.removeEventListener('click', onClick);
            };
        });

        return (
            <div
                className={getClassName(
                    styles.modal,
                    styles.modal__overlay,
                    keepHeaderAboveOverlay
                        ? styles['under-header-overlay']
                        : '',
                    fullscreen ? styles['modal--fullscreen'] : '',
                    className,
                )}
                role='dialog'
                aria-modal='true'
                aria-label={ariaLabel}
                ref={modalRef}
            >
                <div
                    className={getClassName(
                        styles.modal__container,
                        containerClassName,
                    )}
                    style={anchorPosition}
                >
                    <div ref={ref} className={styles.modal__scroll}>
                        <div
                            className={getClassName(
                                styles.modal__content,
                                contentClassName,
                            )}
                        >
                            {onClose && showClose ? (
                                <button
                                    className={getClassName(
                                        styles.modal__close,
                                        closeButtonClassName,
                                    )}
                                    onClick={onClose}
                                    aria-label='Close'
                                    type='button'
                                    {...endToEndTestIDParams}
                                />
                            ) : null}
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

/**
 * @type {React.ForwardRefExoticComponent<ModalProps & React.RefAttributes<any>>}
 */
const Modal = forwardRef((props, ref) => {
    const {
        onClose = null,
        children,
        parentId = window.STORYBOOK ? '' : 'modal-root',
        className = null,
        containerClassName = null,
        contentClassName,
        closeButtonClassName,
        fullscreen = false,
        lock = true,
        showClose = true,
        anchorPosition = null,
        keepHeaderAboveOverlay = false,
        returnFocusOnClose = true,
        allowDismissal = true,
        'aria-label': ariaLabel,
        fallbackFocusRef = null,
    } = props;
    const modalRef = useRef(null);
    const scrollRef = useRef();
    const childRef = useRef(null);

    useEffect(() => {
        scrollRef.current = modalRef.current;
        if (!ref) {
            childRef.current = modalRef.current;
            return;
        }
        const modifiedRef = ref;
        modifiedRef.current = modalRef.current;
    }, [ref]);

    useEffect(() => clearAllBodyScrollLocks, []);

    const enableScroll = useCallback(() => {
        if (!lock) {
            return;
        }

        if (!scrollRef.current) {
            return;
        }

        enableBodyScroll(scrollRef.current);
    }, [lock]);

    useEffect(() => {
        if (!lock) {
            return noop;
        }

        const scrollElement = scrollRef.current;
        if (!scrollElement) {
            return noop;
        }

        disableBodyScroll(scrollElement, {
            reserveScrollBarGap: true,
            hideBodyOverflow: true,
            allowTouchMove: (element) => {
                if (!scrollElement.contains(element)) {
                    return false;
                }

                if (!isInsideScrollableContainer(element, scrollElement)) {
                    return false;
                }

                return true;
            },
        });

        return () => {
            enableScroll();
        };
    }, [enableScroll, lock]);

    const handleClose = useCallback(() => {
        if (onClose) onClose();
    }, [onClose]);

    return (
        <Portal parentId={parentId}>
            <FocusTrap
                focusTrapOptions={{
                    allowOutsideClick: allowDismissal,
                    // https://github.com/focus-trap/focus-trap/blob/master/docs/js/animated-dialog.js
                    checkCanFocusTrap: (trapContainers) => {
                        const results = trapContainers.map(
                            (trapContainer) =>
                                new Promise((resolve) => {
                                    const interval = setInterval(() => {
                                        if (
                                            window.getComputedStyle(
                                                trapContainer,
                                            ).visibility !== 'hidden'
                                        ) {
                                            resolve();
                                            clearInterval(interval);
                                        }
                                    }, 5);
                                }),
                        );
                        // Return a promise that resolves when all the trap containers are able to receive focus
                        return Promise.all(results);
                    },
                    fallbackFocus: () =>
                        (fallbackFocusRef && fallbackFocusRef.current) ||
                        document.getElementById('modal-root'),
                    returnFocusOnDeactivate: returnFocusOnClose,
                }}
            >
                <StatelessModal
                    ref={modalRef}
                    onClose={handleClose}
                    parentId={parentId}
                    className={className}
                    containerClassName={containerClassName}
                    contentClassName={contentClassName}
                    closeButtonClassName={closeButtonClassName}
                    fullscreen={fullscreen}
                    showClose={showClose}
                    anchorPosition={anchorPosition}
                    keepHeaderAboveOverlay={keepHeaderAboveOverlay}
                    allowDismissal={allowDismissal}
                    aria-label={ariaLabel}
                >
                    {children}
                </StatelessModal>
            </FocusTrap>
        </Portal>
    );
});

export default Modal;
